import { useEffect } from "react";
import axios from "axios";

import { endsSession } from "../../api/session-failure";

/**
 * LF360-1970 — sign the user out when, and only when, the server says there is
 * no session.
 *
 * Both layouts had this effect, written out twice, and the two copies had
 * already drifted: one registered the interceptor in the render body and never
 * ejected it, so a single refusal ran the callback once per accumulated copy.
 * One hook, so the rule and its lifetime are in one place.
 *
 * `endsSession` is what decides; see `api/session-failure.js` for why a bare
 * `401 || 403` was the wrong question.
 *
 * @param {() => void} onSessionLost run once per refusal that means the session
 *   is gone. Must be stable — `useCallback` at the call site — or the
 *   interceptor is torn down and rebuilt on every render.
 */
export const useSessionInterceptor = (onSessionLost) => {
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (endsSession(error)) {
          onSessionLost();
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(id);
  }, [onSessionLost]);
};

export default useSessionInterceptor;
